/**
 * Registre des types d'événements généalogiques
 * Architecture extensible pour gérer tous les types d'événements de la vie
 */

/**
 * Catégories d'événements avec leurs propriétés
 */
export const EVENT_CATEGORIES = {
    FAMILY: 'family',
    PROFESSIONAL: 'professional', 
    EDUCATIONAL: 'educational',
    DISTINCTION: 'distinction',
    MEDIA: 'media',
    GEOGRAPHIC: 'geographic',
    PERSONAL: 'personal'
};

/**
 * Définition complète des types d'événements
 * Structure extensible avec métadonnées et configuration
 */
export const EVENT_TYPES = {
    // === ÉVÉNEMENTS FAMILIAUX ===
    birth: {
        category: EVENT_CATEGORIES.FAMILY,
        label: 'Naissance',
        fields: ['date', 'townKey'],
        optionalFields: [],
        hasLocation: true,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 1,
        timelineDisplay: true,
        fanChartDisplay: true,
        color: '#4CAF50'
    },
    death: {
        category: EVENT_CATEGORIES.FAMILY,
        label: 'Décès',
        fields: ['date', 'townKey'],
        optionalFields: ['age'],
        hasLocation: true,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 1,
        timelineDisplay: true,
        fanChartDisplay: true,
        color: '#795548'
    },
    baptism: {
        category: EVENT_CATEGORIES.FAMILY,
        label: 'Baptême',
        fields: ['date', 'townKey'],
        optionalFields: ['church', 'godparents'],
        hasLocation: true,
        hasAttendees: true,
        hasRelatedPerson: false,
        priority: 2,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#2196F3'
    },
    marriage: {
        category: EVENT_CATEGORIES.FAMILY,
        label: 'Mariage',
        fields: ['date', 'townKey'],
        optionalFields: ['spouseId', 'witnesses'],
        hasLocation: true,
        hasAttendees: true,
        hasRelatedPerson: true,
        priority: 1,
        timelineDisplay: true,
        fanChartDisplay: true,
        color: '#E91E63'
    },
    divorce: {
        category: EVENT_CATEGORIES.FAMILY,
        label: 'Divorce',
        fields: ['date', 'townKey'],
        optionalFields: ['spouseId', 'reason'],
        hasLocation: true,
        hasAttendees: false,
        hasRelatedPerson: true,
        priority: 3,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#FF5722'
    },
    adoption: {
        category: EVENT_CATEGORIES.FAMILY,
        label: 'Adoption',
        fields: ['date', 'townKey'],
        optionalFields: ['adoptiveParents', 'birthParents'],
        hasLocation: true,
        hasAttendees: true,
        hasRelatedPerson: true,
        priority: 2,
        timelineDisplay: true,
        fanChartDisplay: true,
        color: '#9C27B0'
    },
    'child-birth': {
        category: EVENT_CATEGORIES.FAMILY,
        label: 'Naissance d\'enfant',
        fields: ['date', 'childId'],
        optionalFields: ['townKey'],
        hasLocation: false,
        hasAttendees: false,
        hasRelatedPerson: true,
        priority: 2,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#4CAF50'
    },

    // === ÉVÉNEMENTS PROFESSIONNELS ===
    occupation: {
        category: EVENT_CATEGORIES.PROFESSIONAL,
        label: 'Profession',
        fields: ['date', 'occupation'],
        optionalFields: ['townKey', 'employer', 'salary'],
        hasLocation: false,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 3,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#607D8B'
    },
    promotion: {
        category: EVENT_CATEGORIES.PROFESSIONAL,
        label: 'Promotion',
        fields: ['date', 'newPosition'],
        optionalFields: ['townKey', 'employer', 'previousPosition'],
        hasLocation: false,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 4,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#FF9800'
    },
    retirement: {
        category: EVENT_CATEGORIES.PROFESSIONAL,
        label: 'Retraite',
        fields: ['date'],
        optionalFields: ['townKey', 'employer', 'pension'],
        hasLocation: false,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 3,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#8BC34A'
    },
    'business-creation': {
        category: EVENT_CATEGORIES.PROFESSIONAL,
        label: 'Création d\'entreprise',
        fields: ['date', 'businessName'],
        optionalFields: ['townKey', 'businessType', 'partners'],
        hasLocation: true,
        hasAttendees: true,
        hasRelatedPerson: false,
        priority: 3,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#3F51B5'
    },

    // === ÉVÉNEMENTS ÉDUCATIFS ===
    education: {
        category: EVENT_CATEGORIES.EDUCATIONAL,
        label: 'Éducation',
        fields: ['date', 'institution'],
        optionalFields: ['townKey', 'degree', 'field'],
        hasLocation: true,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 4,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#009688'
    },
    diploma: {
        category: EVENT_CATEGORIES.EDUCATIONAL,
        label: 'Diplôme',
        fields: ['date', 'diplomaName'],
        optionalFields: ['townKey', 'institution', 'honors'],
        hasLocation: true,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 3,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#00BCD4'
    },
    graduation: {
        category: EVENT_CATEGORIES.EDUCATIONAL,
        label: 'Remise de diplôme',
        fields: ['date', 'institution'],
        optionalFields: ['townKey', 'degree', 'honors'],
        hasLocation: true,
        hasAttendees: true,
        hasRelatedPerson: false,
        priority: 3,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#03A9F4'
    },
    certification: {
        category: EVENT_CATEGORIES.EDUCATIONAL,
        label: 'Certification',
        fields: ['date', 'certificationName'],
        optionalFields: ['townKey', 'organization', 'validity'],
        hasLocation: false,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 4,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#2196F3'
    },

    // === DISTINCTIONS ===
    decoration: {
        category: EVENT_CATEGORIES.DISTINCTION,
        label: 'Décoration',
        fields: ['date', 'decorationName'],
        optionalFields: ['townKey', 'authority', 'reason'],
        hasLocation: true,
        hasAttendees: true,
        hasRelatedPerson: false,
        priority: 2,
        timelineDisplay: true,
        fanChartDisplay: true,
        color: '#FFD700'
    },
    award: {
        category: EVENT_CATEGORIES.DISTINCTION,
        label: 'Récompense',
        fields: ['date', 'awardName'],
        optionalFields: ['townKey', 'organization', 'reason'],
        hasLocation: true,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 3,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#FFC107'
    },
    recognition: {
        category: EVENT_CATEGORIES.DISTINCTION,
        label: 'Reconnaissance',
        fields: ['date', 'recognitionType'],
        optionalFields: ['townKey', 'authority', 'description'],
        hasLocation: false,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 4,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#FF9800'
    },
    title: {
        category: EVENT_CATEGORIES.DISTINCTION,
        label: 'Titre',
        fields: ['date', 'titleName'],
        optionalFields: ['townKey', 'authority', 'hereditary'],
        hasLocation: true,
        hasAttendees: true,
        hasRelatedPerson: false,
        priority: 2,
        timelineDisplay: true,
        fanChartDisplay: true,
        color: '#9C27B0'
    },

    // === ÉVÉNEMENTS MÉDIATIQUES ===
    'media-mention': {
        category: EVENT_CATEGORIES.MEDIA,
        label: 'Mention médiatique',
        fields: ['date', 'mediaType'],
        optionalFields: ['townKey', 'publication', 'topic'],
        hasLocation: false,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 5,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#795548'
    },
    publication: {
        category: EVENT_CATEGORIES.MEDIA,
        label: 'Publication',
        fields: ['date', 'publicationName'],
        optionalFields: ['townKey', 'publisher', 'isbn'],
        hasLocation: false,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 4,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#607D8B'
    },
    interview: {
        category: EVENT_CATEGORIES.MEDIA,
        label: 'Interview',
        fields: ['date', 'interviewer'],
        optionalFields: ['townKey', 'media', 'topic'],
        hasLocation: true,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 4,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#9E9E9E'
    },
    'public-speech': {
        category: EVENT_CATEGORIES.MEDIA,
        label: 'Discours public',
        fields: ['date', 'occasion'],
        optionalFields: ['townKey', 'audience', 'topic'],
        hasLocation: true,
        hasAttendees: true,
        hasRelatedPerson: false,
        priority: 3,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#673AB7'
    },

    // === ÉVÉNEMENTS GÉOGRAPHIQUES ===
    migration: {
        category: EVENT_CATEGORIES.GEOGRAPHIC,
        label: 'Migration',
        fields: ['date', 'destinationKey'],
        optionalFields: ['originKey', 'reason', 'transportMode'],
        hasLocation: true,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 3,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#FF5722'
    },
    residence: {
        category: EVENT_CATEGORIES.GEOGRAPHIC,
        label: 'Résidence',
        fields: ['date', 'townKey'],
        optionalFields: ['address', 'propertyType', 'duration'],
        hasLocation: true,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 4,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#8BC34A'
    },
    travel: {
        category: EVENT_CATEGORIES.GEOGRAPHIC,
        label: 'Mission',
        fields: ['date', 'destinationKey'],
        optionalFields: ['originKey', 'purpose', 'duration'],
        hasLocation: true,
        hasAttendees: true,
        hasRelatedPerson: false,
        priority: 5,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#00BCD4'
    },

    // === ÉVÉNEMENTS PERSONNELS ===
    illness: {
        category: EVENT_CATEGORIES.PERSONAL,
        label: 'Maladie',
        fields: ['date'],
        optionalFields: ['townKey', 'illness', 'doctor'],
        hasLocation: false,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 4,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#F44336'
    },
    recovery: {
        category: EVENT_CATEGORIES.PERSONAL,
        label: 'Guérison',
        fields: ['date'],
        optionalFields: ['townKey', 'illness', 'treatment'],
        hasLocation: false,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 4,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#4CAF50'
    },
    'military-service': {
        category: EVENT_CATEGORIES.PERSONAL,
        label: 'Service militaire',
        fields: ['date'],
        optionalFields: ['townKey', 'regiment', 'rank', 'duration'],
        hasLocation: true,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 3,
        timelineDisplay: true,
        fanChartDisplay: false,
        color: '#795548'
    },

    // === ÉVÉNEMENT SPÉCIAL ===
    today: {
        category: EVENT_CATEGORIES.FAMILY,
        label: 'Aujourd\'hui',
        fields: ['date'],
        optionalFields: ['age'],
        hasLocation: false,
        hasAttendees: false,
        hasRelatedPerson: false,
        priority: 1,
        timelineDisplay: false,
        fanChartDisplay: true,
        color: '#9C27B0'
    }
};

/**
 * Utilitaires pour le registre des événements
 */
export const EventRegistry = {
    /**
     * Obtient la définition d'un type d'événement
     */
    getEventType(type) {
        return EVENT_TYPES[type];
    },

    /**
     * Vérifie si un type d'événement existe
     */
    isValidEventType(type) {
        return Object.prototype.hasOwnProperty.call(EVENT_TYPES, type);
    },

    /**
     * Obtient tous les types d'événements d'une catégorie
     */
    getEventsByCategory(category) {
        return Object.entries(EVENT_TYPES)
            .filter(([_, config]) => config.category === category)
            .reduce((acc, [type, config]) => ({ ...acc, [type]: config }), {});
    },

    /**
     * Obtient les événements visibles dans la timeline
     */
    getTimelineEvents() {
        return Object.entries(EVENT_TYPES)
            .filter(([_, config]) => config.timelineDisplay)
            .map(([type]) => type);
    },

    /**
     * Obtient les événements visibles dans le fan chart
     */
    getFanChartEvents() {
        return Object.entries(EVENT_TYPES)
            .filter(([_, config]) => config.fanChartDisplay)
            .map(([type]) => type);
    },

    /**
     * Obtient les événements par priorité
     */
    getEventsByPriority(priority) {
        return Object.entries(EVENT_TYPES)
            .filter(([_, config]) => config.priority === priority)
            .map(([type]) => type);
    },

    /**
     * Obtient les champs requis pour un type d'événement
     */
    getRequiredFields(type) {
        const eventType = EVENT_TYPES[type];
        return eventType ? eventType.fields : [];
    },

    /**
     * Obtient tous les champs pour un type d'événement (requis + optionnels)
     */
    getAllFields(type) {
        const eventType = EVENT_TYPES[type];
        if (!eventType) return [];
        return [...eventType.fields, ...eventType.optionalFields];
    },

    /**
     * Valide la structure d'un événement
     */
    validateEvent(event) {
        if (!event.type || !this.isValidEventType(event.type)) {
            return { valid: false, error: 'Type d\'événement invalide' };
        }

        const eventType = EVENT_TYPES[event.type];
        const requiredFields = eventType.fields;
        
        for (const field of requiredFields) {
            if (!Object.prototype.hasOwnProperty.call(event, field) || event[field] === undefined) {
                return { valid: false, error: `Champ requis manquant: ${field}` };
            }
            // Accepter 'date inconnue' comme valeur valide pour le champ date
            if (field === 'date' && event[field] === '') {
                return { valid: false, error: `Date ne peut pas être vide (utiliser 'date inconnue')` };
            }
        }

        return { valid: true };
    }
};

/**
 * Configuration d'affichage pour les différentes catégories
 */
export const CATEGORY_CONFIG = {
    [EVENT_CATEGORIES.FAMILY]: {
        label: 'Événements familiaux',
        icon: '👥',
        color: '#4CAF50'
    },
    [EVENT_CATEGORIES.PROFESSIONAL]: {
        label: 'Événements professionnels', 
        icon: '💼',
        color: '#607D8B'
    },
    [EVENT_CATEGORIES.EDUCATIONAL]: {
        label: 'Événements éducatifs',
        icon: '🎓',
        color: '#009688'
    },
    [EVENT_CATEGORIES.DISTINCTION]: {
        label: 'Distinctions',
        icon: '🏆',
        color: '#FFD700'
    },
    [EVENT_CATEGORIES.MEDIA]: {
        label: 'Événements médiatiques',
        icon: '📺',
        color: '#795548'
    },
    [EVENT_CATEGORIES.GEOGRAPHIC]: {
        label: 'Événements géographiques',
        icon: '🗺️',
        color: '#FF5722'
    },
    [EVENT_CATEGORIES.PERSONAL]: {
        label: 'Événements personnels',
        icon: '🏥',
        color: '#F44336'
    }
};